import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import SupportButton from './support/SupportButton';
import {
  ChevronLeft, ChevronRight, ChevronDown, Edit, ExternalLink, Copy, Play, LifeBuoy,
  AlertCircle, CheckCircle, ClipboardList, RefreshCw, MessageSquarePlus, GitCompare, Terminal,
} from 'lucide-react';
import ReprocessProfilesModal from './shared/ReprocessProfilesModal';
import { parseMissingProfiles } from '../utils/missingProfiles';
import useAutoRefresh from '../hooks/useAutoRefresh';
import syncService, { SPR_NOTE_PREFIX } from '../services/syncService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import ExchangeViewer from './shared/ExchangeViewer';
import SearchablePayload from './shared/SearchablePayload';
import orderMessageLog from './shared/messageLogOrder';
import useErrorKb from './shared/useErrorKb';
import {
  classifyStoryLine, prettyJson, getEntryExchanges, prettyLogData, decodePayloadB64,
} from './shared/messageLogUtils';
import { formatDateTime } from '../utils/datetime';
import DiffModal from './shared/DiffModal';
import RecordEditModal from './RecordEditModal';
import OhipSimulateModal from './OhipSimulateModal';
import DvmModal from './DvmModal';
import { useAuth } from '../contexts/AuthContext';
import './TaskConfigurationPage.css';
import './TaskReconciliationPage.css';
import './PaymentPorterPage.css'; // pp-back-btn, sa-only, toggle styles
import './MessageLogPage.css';
import { getRecordStatusColor, getRecordStatusTitle } from '../utils/recordStatus';

// Shared with TaskReconciliationPage and TaskConfigurationPage — see utils/recordStatus.js.
const getReconStatusColor = getRecordStatusColor;

// Full-page message log for one reconciliation record (STYLE.md: detail views are pages, not modals).
// Shared by the task-configuration and reconciliation screens — both navigate here. The record list
// (for Prev/Next ordering) comes from location.state when the caller passes it, else it is fetched
// with the caller's filters so deep links work too.
function MessageLogPage() {
  const { syncId, taskId, recordUuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isSuperAdmin = !!user?.isSuperAdmin;
  const backTo = location.state?.backTo || `/sync/${syncId}/task/${taskId}`;

  const [task, setTask] = useState(null);
  const [records, setRecords] = useState(location.state?.records || []);
  // Set once the task's record list has come back, so the "not in this task" notice below can tell the
  // difference between "still loading" and "genuinely not here".
  const [recordsLoaded, setRecordsLoaded] = useState(false);
  const [messageLog, setMessageLog] = useState([]);
  const [fullLog, setFullLog] = useState([]); // every attempt (non-deduped) — drives the Status History cards
  // Set when a log fetch FAILED, as distinct from returning nothing. The empty state below reads very
  // differently depending on which it was.
  const [logError, setLogError] = useState(null);
  const [logLoading, setLogLoading] = useState(true);
  const [tab, setTab] = useState('messages'); // 'messages' | 'history'
  const [selectedExchange, setSelectedExchange] = useState({}); // entryIndex -> selected call index
  const [decodedPayloads, setDecodedPayloads] = useState({});
  const [expanded, setExpanded] = useState({}); // entryIndex -> bool (overrides default)
  const [editOpen, setEditOpen] = useState(false);
  const [simulateSeed, setSimulateSeed] = useState(null);
  const [dvmSeed, setDvmSeed] = useState(null);
  const [toast, setToast] = useState(null);

  // Heavy transcripts (ohipExchanges/request/response) are offloaded to S3 for large entries and only
  // referenced (payload_s3_key) in the log — keeps the list fast. Lazy-fetch a payload the first time an
  // offloaded entry is opened, cache it by key, and merge it back into the entry's data for the viewer.
  const [hydratedPayloads, setHydratedPayloads] = useState({}); // payload_s3_key -> merged data
  const [hydratingKeys, setHydratingKeys]       = useState({}); // payload_s3_key -> bool
  const hydrateEntry = async (entry) => {
    const k = entry && entry.payload_s3_key;
    if (!entry?.payload_offloaded || !k || hydratedPayloads[k] || hydratingKeys[k]) return;
    setHydratingKeys(s => ({ ...s, [k]: true }));
    try {
      const payload = await syncService.getReconciliationMessageLogPayload(syncId, taskId, entry.payload_s3_bucket, k);
      setHydratedPayloads(s => ({ ...s, [k]: { ...entry.data, ...payload } }));
    } catch (e) {
      setHydratedPayloads(s => ({ ...s, [k]: { ...entry.data } })); // degrade to story-only on failure
    } finally {
      setHydratingKeys(s => ({ ...s, [k]: false }));
    }
  };

  // EXPORT THE OHIP REQUESTS (superadmin) — every call this record made to OHIP, across every attempt, as a bash
  // script of curls for Oracle support. Built and stored on the server (it can run to megabytes, and the log this
  // page holds has its transcripts stripped); this asks for it and follows the presigned link it returns.
  const [curlExporting, setCurlExporting] = useState(false);
  const exportOhipCurls = async () => {
    setCurlExporting(true);
    try {
      const r = await syncService.getOhipCurlExport(syncId, taskId, recordUuid);
      if (r && r.url) window.location.assign(r.url);
      setToast({ type: 'success', message: `${r.calls} OHIP request${r.calls === 1 ? '' : 's'} exported (${Math.ceil((r.bytes || 0) / 1024)} KB)` });
    } catch (e) {
      setToast({ type: 'error', message: e.response?.data?.error?.message || e.message || 'The export failed' });
    } finally {
      setCurlExporting(false);
    }
  };

  // ENTRY → EXIT diff. Holds the PAIR OF ENTRIES, not their text: an offloaded payload arrives after the
  // modal opens, and keeping the objects means the text is recomputed from hydratedPayloads on each
  // render, so the diff fills itself in when S3 responds instead of freezing on whatever was loaded at
  // click time.
  const [diffFor, setDiffFor] = useState(null);

  // The other half of a step's pair. Matched on lambda AND transaction: the log accumulates across every
  // reprocess, so matching on lambda alone would diff today's entry against a run from days ago.
  const counterpartOf = (entry, wantedDirection) => {
    if (!entry) return null;
    const lambda = entry.lambda || entry.lambdaName;
    const tx = entry.transactionId || entry.transaction_id || null;
    return (Array.isArray(messageLog) ? messageLog : []).find(e =>
      (e.lambda || e.lambdaName) === lambda
      && String(e.direction || '').toLowerCase() === wantedDirection
      && (e.transactionId || e.transaction_id || null) === tx
    ) || null;
  };

  // The decoded, pretty-printed payload for one entry, or null when it is offloaded and not yet fetched.
  // NULL IS NOT '': an empty string would diff as "identical" and quietly tell the user nothing changed
  // when the truth is that nothing has loaded yet.
  const entryPayloadText = (entry) => {
    if (!entry) return null;
    const k = entry.payload_s3_key;
    if (entry.payload_offloaded && !hydratedPayloads[k]) return null;
    const data = (entry.payload_offloaded && hydratedPayloads[k]) ? hydratedPayloads[k] : entry.data;
    const b64 = data && data.payloadB64;
    if (!b64) return '(no payload recorded)';
    try { return decodePayloadB64(b64); } catch (e) { return `[Decode error: ${e.message}]`; }
  };

  // Add-comment to this record's SPR status history.
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);

  // Support tickets raised about a record this session: uuid -> hub ticket id (support/SupportButton).
  const [supportTickets, setSupportTickets] = useState({});

  // Reprocess (same behaviour as the parent list): if the record's error names missing source
  // profiles, offer to request them first via the shared wizard, then queue the reprocess.
  const [reproWizardItems, setReproWizardItems] = useState(null);
  const [reproConfirmOpen, setReproConfirmOpen] = useState(false);
  const [reproRunning, setReproRunning] = useState(false);

  // Manual on-site instruction (superadmin): stamped on the SPR record and picked up by the
  // On-site Instructions report / email.
  const [instrModalOpen, setInstrModalOpen] = useState(false);
  const [instrText, setInstrText] = useState('');
  const [instrSaving, setInstrSaving] = useState(false);
  const [instrError, setInstrError] = useState(null);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const { renderKbTriggerFor, kbModalElement } = useErrorKb(task?.syncTemplateId, isSuperAdmin);

  const uuidOf = (r) => r && (r.sprRecordUuid || r.recordUuid);

  const record = useMemo(
    () => records.find(r => uuidOf(r) === recordUuid) || null,
    [records, recordUuid]
  );
  const recordIndex = useMemo(
    () => records.findIndex(r => uuidOf(r) === recordUuid),
    [records, recordUuid]
  );

  // Last known good copy of the record being viewed, and where it sat in the sibling list. Needed
  // because the record can drop OUT of the list it was navigated from — see fetchRecords.
  const lastKnownRef = useRef({ record: null, index: -1 });
  useEffect(() => {
    if (record) lastKnownRef.current = { record, index: recordIndex };
  }, [record, recordIndex]);

  const fetchRecords = async () => {
    try {
      const filters = location.state?.filters || {};
      const result = await syncService.searchReconciliationRecords(syncId, taskId, filters);
      let list = Array.isArray(result) ? result : (result?.records || []);

      // You almost always arrive here from a FILTERED list (typically recordStatus=ERROR). Reprocessing
      // changes the status, so the very next refetch no longer returns the record you are looking at —
      // recordIndex becomes -1, which disables Prev/Next and blanks the header. Auto-refresh re-runs this
      // every 5s, so background reprocessing does it even without pressing anything.
      // Keep the record pinned at the position it held, and refresh it on its own (by source id, with no
      // status filter) so its status still updates live — which is the whole point of watching it.
      if (recordUuid && !list.some(r => uuidOf(r) === recordUuid)) {
        const { record: known, index: at } = lastKnownRef.current;
        if (known) {
          let fresh = known;
          const sourceId = known.sourcerecordid || known.sourceRecordId;
          if (sourceId) {
            try {
              const res = await syncService.searchReconciliationRecords(syncId, taskId, { sourceRecordId: sourceId });
              const arr = Array.isArray(res) ? res : (res?.records || []);
              fresh = arr.find(r => uuidOf(r) === recordUuid) || known;
            } catch (_) { /* keep the last known copy rather than losing the record */ }
          }
          list = list.slice();
          list.splice(at >= 0 && at <= list.length ? at : list.length, 0, fresh);
        }
      }
      setRecords(list);
    } catch (err) {
      console.error('Failed to load records:', err);
    } finally {
      setRecordsLoaded(true);
    }
  };

  useEffect(() => {
    (async () => {
      try { setTask(await syncService.getTaskDetails(syncId, taskId)); } catch (_) { /* KB/title only */ }
    })();
  }, [syncId, taskId]); // eslint-disable-line

  // Always refetch on open AND on Prev/Next so the record's status/comment are fresh — any
  // records passed via location.state only serve as an instant first paint.
  useEffect(() => { fetchRecords(); }, [syncId, taskId, recordUuid]); // eslint-disable-line

  // Auto-refresh: poll the record + message log every 5s while the toggle is on (same cadence as the
  // records screen). The ref always points at the latest closures so the poll uses current params.
  const refreshRef = useRef(() => {});
  refreshRef.current = async () => {
    fetchRecords();
    try {
      const entries = await syncService.getReconciliationMessageLog(syncId, taskId, recordUuid);
      setMessageLog(Array.isArray(entries) ? entries : []);
    } catch (_) { /* keep the last good log on a failed poll */ }
  };
  // Auto-refresh, on by default (HIL-87). Paused while the tab is hidden, and remembered if
  // switched off — see useAutoRefresh.
  const [autoRefresh, setAutoRefresh] = useAutoRefresh(
    'sync:logAutoRefresh', () => refreshRef.current(), 5000);

  // Flash the screen when THIS record gets (re)processed — i.e. its lastSyncTime (updated_at) advances.
  // Colour-coded by the new status (green processed / red error / blue otherwise). Reset on Prev/Next so
  // switching records never triggers a false flash.
  const [flash, setFlash] = useState(null); // null | status string
  const prevSyncRef = useRef({ uuid: null, time: null });
  const flashTimer = useRef(null);
  useEffect(() => {
    if (!record) return;
    const t = record.lastSyncTime || null;
    const prev = prevSyncRef.current;
    if (prev.uuid === recordUuid && prev.time && t && t !== prev.time) {
      setFlash(record.recordStatus || 'UPDATED');
      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(null), 900);
    }
    prevSyncRef.current = { uuid: recordUuid, time: t };
  }, [record?.lastSyncTime, recordUuid]); // eslint-disable-line
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  // Manual refresh — reload the record + message log once (same work the 5s poll does), independent of
  // the auto-refresh toggle.
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const [, entries, full] = await Promise.all([
        fetchRecords(),
        syncService.getReconciliationMessageLog(syncId, taskId, recordUuid),
        syncService.getReconciliationMessageLog(syncId, taskId, recordUuid, true),
      ]);
      setMessageLog(Array.isArray(entries) ? entries : []);
      setFullLog(Array.isArray(full) ? full : []);
    } catch (_) { /* keep the last good view on a failed refresh */ }
    finally { setRefreshing(false); }
  };

  useEffect(() => {
    (async () => {
      setMessageLog([]);
      setFullLog([]);
      setDecodedPayloads({});
      setSelectedExchange({});
      setExpanded({});
      setTab('messages');
      setLogLoading(true);
      try {
        // allSettled, NOT all. These fetch two different views of the same record — the deduped
        // pipeline for Messages, every attempt for Status History — and Promise.all rejects on the
        // first failure, discarding a result that already came back fine. That is exactly what
        // happened on profile 18489: the all=true fetch 502'd on response size, took the perfectly good
        // Messages result down with it, and the page reported "No message log entries found".
        const [entriesRes, fullRes] = await Promise.allSettled([
          syncService.getReconciliationMessageLog(syncId, taskId, recordUuid),
          syncService.getReconciliationMessageLog(syncId, taskId, recordUuid, true),
        ]);
        const entries = entriesRes.status === 'fulfilled' ? entriesRes.value : null;
        const full    = fullRes.status    === 'fulfilled' ? fullRes.value    : null;
        setMessageLog(Array.isArray(entries) ? entries : []);
        setFullLog(Array.isArray(full) ? full : []);
        // Say which view failed and why. "No entries found" and "the request failed" are different
        // facts, and rendering the second as the first is how a broken endpoint reads as an empty
        // record — the reader stops looking.
        const failed = [
          entriesRes.status === 'rejected' ? ['Messages', entriesRes.reason] : null,
          fullRes.status    === 'rejected' ? ['Status History', fullRes.reason] : null,
        ].filter(Boolean);
        setLogError(failed.length
          ? failed.map(([what, err]) =>
              `${what} could not be loaded: ${err?.response?.data?.message || err?.message || 'request failed'}`).join(' · ')
          : null);
      } catch (err) {
        setMessageLog([]);
        setFullLog([]);
        setLogError(err?.response?.data?.message || err?.message || 'The message log could not be loaded');
      } finally {
        setLogLoading(false);
      }
    })();
  }, [syncId, taskId, recordUuid]);

  // Every RUN of this record, newest first. A record reprocessed N times has N runs in the log, each
  // identified by its transaction_id.
  const logRuns = useMemo(() => {
    const byTx = new Map();
    for (const e of (Array.isArray(messageLog) ? messageLog : [])) {
      const tx = e.transactionId || e.transaction_id || '(none)';
      const t = e.timestamp ? new Date(e.timestamp).getTime() : 0;
      const cur = byTx.get(tx) || { tx, first: Infinity, last: 0, hasError: false, count: 0 };
      cur.first = Math.min(cur.first, t || Infinity);
      cur.last = Math.max(cur.last, t);
      cur.count += 1;
      if (String(e.direction || '').toLowerCase() === 'error') cur.hasError = true;
      byTx.set(tx, cur);
    }
    return [...byTx.values()].sort((a, b) => b.last - a.last);
  }, [messageLog]);

  // Which run to show. Defaults to the newest and follows it as new runs arrive.
  const [selectedRunTx, setSelectedRunTx] = useState(null);
  const activeRunTx = selectedRunTx && logRuns.some(r => r.tx === selectedRunTx)
    ? selectedRunTx
    : (logRuns[0] ? logRuns[0].tx : null);

  // Order the log by pipeline step (oxi-enqueue → opera-oxi → oracle-ohip …), ENTRY before EXIT —
  // scoped to ONE run.
  //
  // The log accumulates across every reprocess, and messageLogOrder groups by step, so an unscoped view
  // interleaved all runs: "ohip-enqueue ENTRY" appeared once per run, and a stale ERROR from days ago sat
  // between today's ENTRY and EXIT looking current. Record 26835231 had 68 entries over 5 runs, including
  // payeeInfo errors from before that bug was fixed — indistinguishable from a live failure.
  const orderedLog = useMemo(() => {
    const src = Array.isArray(messageLog) ? messageLog : [];
    const scoped = activeRunTx
      ? src.filter(e => (e.transactionId || e.transaction_id || '(none)') === activeRunTx)
      : src;
    return orderMessageLog(scoped);
  }, [messageLog, activeRunTx]);

  const goToSibling = (delta) => {
    const next = records[recordIndex + delta];
    const uuid = next && (next.sprRecordUuid || next.recordUuid);
    if (uuid) navigate(`/sync/${syncId}/task/${taskId}/reconciliation/${uuid}/log`, { state: location.state, replace: false });
  };

  // ── Support ────────────────────────────────────────────────────────────────
  // The record's ticket, if one was raised (stamped on the record, or this session). The Support button raises one
  // through the sync route, which attaches the record's own evidence — the entry payload, the OHIP exchanges, the
  // OpenSearch lookups — for the support team only (support/SupportButton `submit` adapter).
  const supportTicketId = supportTickets[recordUuid] || record?.supportTicketId || null;
  const raiseRecordTicket = async ({ subject, description, priority }) => {
    const res = await syncService.createJiraTicket(syncId, taskId, {
      recordUuid, pageUrl: window.location.href, subject, note: description, priority,
    });
    setSupportTickets(prev => ({ ...prev, [recordUuid]: res.ticketId }));
    return { ticketId: res.ticketId };
  };

  // ── Reprocess ──────────────────────────────────────────────────────────────
  const handleReprocess = () => {
    if (!record?.recordid?.trim()) { showToast('Record has no source id to reprocess', 'error'); return; }
    const missingProfiles = parseMissingProfiles(record.comment);
    if (missingProfiles.length > 0) { setReproWizardItems([{ record, missingProfiles }]); return; }
    setReproConfirmOpen(true);
  };
  const runReprocess = async (profileIds = []) => {
    setReproConfirmOpen(false);
    setReproWizardItems(null);
    try {
      setReproRunning(true);
      const res = await syncService.bulkActionRecords(syncId, taskId, 'reprocess', [record.recordid.trim()], profileIds);
      const requested = res?.profilesRequested ? `, ${res.profilesRequested} profile(s) requested` : '';
      showToast(`Reprocess queued${requested}`, 'success');
      await fetchRecords();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to reprocess', 'error');
    } finally {
      setReproRunning(false);
    }
  };

  // ── Manual on-site instruction ─────────────────────────────────────────────
  const currentInstruction = record?.onsiteInstruction || record?.onsite_instruction || '';
  const openInstrModal = () => {
    setInstrText(currentInstruction);
    setInstrError(null);
    setInstrModalOpen(true);
  };
  const saveInstruction = async () => {
    if (instrSaving) return;
    setInstrModalOpen(false);
    setInstrError(null);
    try {
      setInstrSaving(true);
      await syncService.updateReconciliationRecordFields(syncId, taskId, recordUuid, { onsite_instruction: instrText.trim() });
      showToast(instrText.trim() ? 'Site instruction saved — it will appear in the On-site Instructions report' : 'Site instruction removed', 'success');
      await fetchRecords();
    } catch (err) {
      setInstrError(err.response?.data?.message || err.message || 'Failed to save instruction');
    } finally {
      setInstrSaving(false);
    }
  };

  // A story line/exchange that indicates a MISSING DATA VALUE MAPPING gets a DVM button (superadmin)
  // that opens the DVM tool preseeded. Extensible: add more signatures as they are identified.
  //   (1) CRM11012 "✗ REJECTED [NEWS/FIN]" → the [type/value] names the unmapped preference combination.
  //   (2) OPERAWS-GEN01346 "Invalid value of: <field>" → the target DVM has no mapping for the value the
  //       source sent for <field> (e.g. phoneUseType on a block/profile). Field comes from the response
  //       error detail; the offending value from the matching field in the request body.
  const asText = (v) => (v == null ? '' : (typeof v === 'string' ? v : (() => { try { return JSON.stringify(v); } catch (e) { return String(v); } })()));
  // Best-effort map from an OHIP body field to a DVM conversion-code substring (the modal preselects the
  // first conversion code whose name contains this hint). Falls back to the field name upper-cased.
  const DVM_FIELD_HINTS = { phoneUseType: 'PHONE', phoneTechType: 'PHONE', addressType: 'ADDRESS', emailType: 'EMAIL', country: 'COUNTRY', nationality: 'NATION', language: 'LANGUAGE' };
  const parseDvmIssue = (line, ex) => {
    const headers = (ex && ex.requestHeaders) || {};
    const base = {
      externalSystem: headers['x-externalsystem'] || '',
      hotelId: headers['x-hotelid'] || '',
      enterpriseId: headers['x-enterpriseid'] || '',
      appKey: headers['x-app-key'] || '',
      line: line.trim(),
    };

    // (1) Preference-combination rejection.
    if (/CRM11012|Invalid Preference Type Combination|not mapped|no mapping/i.test(line)) {
      const m = line.match(/REJECTED\s+\[([^\]/]+)\/([^\]]+)\]/);
      return { ...base, typeCode: m ? m[1].trim() : null, value: m ? m[2].trim() : null, codeHint: 'PREFERENCE' };
    }

    // (2) "Invalid value of: <field>" (OPERAWS-GEN01346) — read the field from the response, the value
    //     from the request. Works off the exchange, so it fires even when the story line is just "→ 400".
    const respText = asText(ex && ex.response);
    if (/OPERAWS-GEN01346/.test(respText) || /Invalid value of:/i.test(respText) || /Invalid value of:/i.test(line)) {
      const fm = (respText.match(/Invalid value of:\s*([A-Za-z0-9_]+)/) || line.match(/Invalid value of:\s*([A-Za-z0-9_]+)/));
      if (fm) {
        const field = fm[1];
        const vm = asText(ex && ex.request).match(new RegExp('"' + field + '"\\s*:\\s*"([^"]+)"'));
        return { ...base, typeCode: null, field, value: vm ? vm[1] : null, codeHint: DVM_FIELD_HINTS[field] || field.toUpperCase() };
      }
    }
    return null;
  };

  // ── Story / exchange rendering (ported from the old overlay) ───────────────
  const renderStory = (entry, entryIndex) => {
    const story = entry.data && entry.data.story;
    if (!story || !String(story).trim()) return null;
    const lines = String(story).split('\n').map(l => l.replace(/\s+$/, '')).filter(l => l.trim() !== '');
    // Offloaded entries keep the story inline but their exchanges live in S3 — merge the lazily-fetched
    // payload back in when available; until then the lines are still shown (and clickable to load).
    const k = entry.payload_s3_key;
    const effectiveData = (entry.payload_offloaded && hydratedPayloads[k]) ? hydratedPayloads[k] : entry.data;
    const exchanges = getEntryExchanges({ ...entry, data: effectiveData });
    const offloadedPending = !!(entry.payload_offloaded && !hydratedPayloads[k]);
    const offloadedLoading = !!(entry.payload_offloaded && hydratingKeys[k]);
    const sel = selectedExchange[entryIndex];
    return (
      <div className="message-log-story">
        {lines.map((line, i) => {
          const ex = exchanges[i];
          const isSel = sel === i;
          // A 2xx call that OHIP flagged (✗ REJECTED / ⚠ / not processed) classifies as 'warning' —
          // oracle-ohip counts these toward marking the whole record ERROR. Keep the line yellow, but
          // badge it so the user sees this specific call is why the record was marked as an error.
          const cls = classifyStoryLine(line);
          return (
            <div key={i} className={`story-step story-step-${cls}${isSel ? ' story-step-selected' : ''}`}>
              <span
                className={`story-step-text${(ex || offloadedPending) ? ' story-step-clickable' : ''}`}
                title={ex ? 'Click to show only this call’s request & response below' : (offloadedPending ? 'Click to load this call’s request & response (stored in S3)' : line)}
                onClick={
                  offloadedPending ? () => { hydrateEntry(entry); setSelectedExchange(prev => ({ ...prev, [entryIndex]: i })); }
                    : ex ? () => setSelectedExchange(prev => ({ ...prev, [entryIndex]: prev[entryIndex] === i ? undefined : i }))
                      : undefined
                }
              >{line}</span>
              {offloadedLoading && <span className="story-step-loading">loading transcript…</span>}
              {/* A 2xx line OHIP flagged. What it did to the record depends on the connector's
                  RESPONSE_CODE_POLICY, so read the badge off the record's actual status instead of
                  asserting "Marked as error" — the default for CRM11012 is now WARNING (the profile
                  IS written; one preference was refused), and the old wording said the opposite. */}
              {cls === 'warning' && (() => {
                const rs = String(record?.recordStatus || '').toUpperCase();
                if (rs !== 'ERROR' && rs !== 'WARNING') return null;
                return (
                  <span
                    className={`story-marked-error-badge${rs === 'WARNING' ? ' story-marked-warning-badge' : ''}`}
                    title={rs === 'WARNING'
                      ? 'This call returned a 2xx but OHIP rejected part of it. The record synced and is marked WARNING — no reprocess needed, but the underlying configuration needs fixing.'
                      : 'This call returned a 2xx but was counted as an error, which marked the record as ERROR'}
                  >
                    {rs === 'WARNING' ? 'Marked as warning' : 'Marked as error'}
                  </span>
                );
              })()}
              {renderKbTriggerFor(line)}
              {isSuperAdmin && ex && !ex.noSimulate && (
                <button
                  type="button"
                  className="story-simulate-btn sa-only"
                  title="Replay/edit this request against OHIP (target connector credentials)"
                  onClick={(e) => { e.stopPropagation(); setSimulateSeed({ method: ex.method, url: ex.url, body: ex.request, headers: ex.requestHeaders, record }); }}
                >
                  <Play size={12} /> Simulate
                </button>
              )}
              {isSuperAdmin && parseDvmIssue(line, ex) && (
                <button
                  type="button"
                  className="story-simulate-btn sa-only"
                  title="Read the target's data value mappings and add the missing one"
                  onClick={(e) => { e.stopPropagation(); setDvmSeed(parseDvmIssue(line, ex)); }}
                >
                  DVM
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Status-history / data-points tab content.
    // Whether an entry represents a FAILURE, which is what decides if a non-superadmin may see the
  // processing steps inside it.
  //
  // Two signals, because neither alone is reliable: the log entry's own status (set at write time —
  // 'error' for a failed exit, 'success' otherwise), and the story text, since a record can carry a
  // 4xx/5xx line while the entry status says otherwise. Either one counts.
  const entryIsError = (entry) => {
    if (!entry) return false;
    if (String(entry.status || '').toLowerCase() === 'error') return true;
    const story = entry.data && entry.data.story;
    if (!story) return false;
    return String(story).split('\n').some(l => classifyStoryLine(l) === 'error');
  };

  // Render one message-log entry card. Shared by the Messages tab and the Status History per-attempt
  // cards, so every request — whatever its outcome — gets the same functionality (clickable exchange
  // story with KB+ DVM, Load-full-transcript-from-S3, decode). `opts.headerContent` replaces the default
  // lambda/direction/time header (history cards pass a status badge); `opts.defaultExpanded` seeds the
  // initial expand state.
  const renderLogEntry = (entry, i, opts = {}) => {
    const entryKey = entry.payload_s3_key;
    const effectiveEntryData = (entry.payload_offloaded && hydratedPayloads[entryKey]) ? hydratedPayloads[entryKey] : entry.data;
    const payloadB64 = effectiveEntryData && effectiveEntryData.payloadB64;
    const hasPayload = !!payloadB64;
    const isDecoded = Boolean(decodedPayloads[i]);
    const dir = String(entry.direction || '').toLowerCase();
    const defaultExpanded = opts.defaultExpanded != null ? opts.defaultExpanded : (dir !== 'entry' && dir !== 'exit');
    // IP PROTECTION. The processing steps spell out the exact sequence of calls we make against the
    // target — that is our implementation, not the customer's data. A non-superadmin sees the outcome
    // (the status badge in the header) but not how it was reached, UNLESS the entry failed: an error is
    // the customer's problem to act on and they need the detail to act on it.
    const stepsVisible = isSuperAdmin || entryIsError(entry);
    const isExpanded = stepsVisible && (expanded[i] ?? defaultExpanded);
    // Diff sits on the ENTRY card only — one control per pair, on the card you read first, answering
    // "what did this step do to the record". Gated on stepsVisible for the same IP reason the expand is:
    // the diff IS the payload, so offering it on a collapsed card would hand a non-superadmin exactly
    // what the collapse is there to withhold.
    const pairedExit = dir === 'entry' ? counterpartOf(entry, 'exit') : null;
    // A FAILED STEP HAS TO LOOK FAILED. The direction chip was coloured by direction alone, so a
    // spr-swap-ids EXIT that ended in ERROR rendered green — and its story lines are green too, correctly,
    // because the calls it made (four OpenSearch lookups) really did return 200. The only thing on screen
    // saying otherwise was a 3px left border, so a record that had genuinely errored read as one that was
    // silently stuck. The reason lives in data.note and was not shown at all until the card was expanded.
    const isErr = entryIsError(entry);
    const failNote = isErr
      ? ((effectiveEntryData && (effectiveEntryData.note || effectiveEntryData.errorMessage)) || '')
      : '';
    return (
      <div key={i} className={`message-log-entry message-log-entry-${entry.status || 'processing'}`}>
        <div
          className={`message-log-entry-header${stepsVisible ? ' message-log-entry-header-toggle' : ''}`}
          onClick={stepsVisible ? () => setExpanded(prev => ({ ...prev, [i]: !isExpanded })) : undefined}
          title={stepsVisible ? (isExpanded ? 'Collapse' : 'Expand') : undefined}
        >
          {/* No chevron when there is nothing to open — a control that does nothing reads as broken. */}
          {stepsVisible
            ? (isExpanded ? <ChevronDown size={15} className="message-log-chevron" /> : <ChevronRight size={15} className="message-log-chevron" />)
            : <span className="message-log-chevron-spacer" />}
          {opts.headerContent || (<>
            <span className="message-log-lambda">{entry.lambda || entry.lambdaName || '-'}</span>
            {/* Coloured by OUTCOME, not by direction — the label still says which end of the step it is. */}
            <span className={`message-log-direction message-log-direction-${isErr ? 'error' : entry.direction}`}>
              {entry.direction || '-'}
            </span>
            <span className="message-log-time">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '-'}</span>
            {/* The reason, on the collapsed card. "Missing linked Profile 30482527 (Company)" is the whole
                answer to why the record stopped, and it was one click away from being invisible. */}
            {failNote && <span className="message-log-fail-note" title={failNote}>{failNote}</span>}
          </>)}
          {stepsVisible && pairedExit && (
            <button
              type="button"
              className="message-log-diff-btn"
              title="Compare the payload in with the payload out"
              onClick={(e) => {
                // The header toggles expand — without this the diff would also collapse the card behind it.
                e.stopPropagation();
                hydrateEntry(entry);
                hydrateEntry(pairedExit);
                setDiffFor({ entry, exit: pairedExit });
              }}
            >
              <GitCompare size={13} /> Diff
            </button>
          )}
        </div>
        {isExpanded && (<>
        {renderStory(entry, i)}
        {(() => {
          const sel = selectedExchange[i];
          const k = entry.payload_s3_key;
          const effectiveData = (entry.payload_offloaded && hydratedPayloads[k]) ? hydratedPayloads[k] : entry.data;
          const exchanges = getEntryExchanges({ ...entry, data: effectiveData });
          const ex = (sel != null) ? exchanges[sel] : null;
          if (ex) {
            return (
              <div className="message-log-exchange">
                <div className="message-log-exchange-head">
                  <span>{String(ex.method || '').toUpperCase()} {ex.url} → {ex.status}</span>
                  <button
                    type="button"
                    className="message-log-exchange-back"
                    onClick={() => setSelectedExchange(prev => ({ ...prev, [i]: undefined }))}
                  >Show full message</button>
                </div>
                {/* The four panes moved to shared/ExchangeViewer when the Reconciliation table needed
                    the same view for a user who cannot open this page. Rendered from here so the two
                    cannot drift into disagreeing about what a request looks like. */}
                <ExchangeViewer exchange={ex} />
              </div>
            );
          }
          return effectiveData ? (
            <>
              {entry.payload_offloaded && !hydratedPayloads[k] && (
                <div className="message-log-transcript-loading">
                  <RefreshCw size={13} className="message-log-load-spin" /> Loading transcript…
                </div>
              )}
              <pre className="message-log-data">{prettyLogData(effectiveData)}</pre>
            </>
          ) : null;
        })()}
        {!hasPayload && entry.data && (
          <div className="message-log-payload-section">
            <button className="message-log-decode-btn" onClick={() => {
              navigator.clipboard.writeText(prettyLogData(effectiveEntryData))
                .then(() => showToast('Copied', 'success'))
                .catch(() => showToast('Copy failed', 'error'));
            }}>
              <Copy size={13} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />Copy
            </button>
          </div>
        )}
        {hasPayload && (
          <div className="message-log-payload-section">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="message-log-decode-btn" onClick={() => {
                if (isDecoded) {
                  setDecodedPayloads(prev => { const n = { ...prev }; delete n[i]; return n; });
                } else {
                  try {
                    setDecodedPayloads(prev => ({ ...prev, [i]: decodePayloadB64(payloadB64) }));
                  } catch (e) {
                    setDecodedPayloads(prev => ({ ...prev, [i]: `[Decode error: ${e.message}]` }));
                  }
                }
              }}>
                {isDecoded ? 'Hide Decoded Payload' : 'Decode payloadB64 & pretty print'}
              </button>
              <button className="message-log-decode-btn" onClick={() => {
                navigator.clipboard.writeText(payloadB64)
                  .then(() => showToast('Encoded (base64) payload copied', 'success'))
                  .catch(() => showToast('Copy failed', 'error'));
              }}>
                <Copy size={13} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />Copy encoded
              </button>
              <button className="message-log-decode-btn" onClick={() => {
                let decoded;
                try { decoded = decodePayloadB64(payloadB64); }
                catch (e) { decoded = `[Decode error: ${e.message}]`; }
                navigator.clipboard.writeText(decoded)
                  .then(() => showToast('Decoded payload copied', 'success'))
                  .catch(() => showToast('Copy failed', 'error'));
              }}>
                <Copy size={13} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />Copy decoded
              </button>
            </div>
            {isDecoded && <SearchablePayload text={decodedPayloads[i]} className="message-log-decoded" />}
          </div>
        )}
        </>)}
      </div>
    );
  };

  // Build one "attempt" per pipeline run (transaction_id) from the full non-deduped log. The
  // representative doc is that run's oracle-ohip EXIT (it carries the OHIP exchanges + the S3 transcript
  // ref); if the run never reached oracle-ohip, fall back to its latest exit/error doc. Newest first.
  const attempts = useMemo(() => {
    const byTx = new Map();
    for (const e of (Array.isArray(fullLog) ? fullLog : [])) {
      const tx = e.transaction_id || e.transactionId;
      if (!tx) continue;
      if (!byTx.has(tx)) byTx.set(tx, []);
      byTx.get(tx).push(e);
    }
    const out = [];
    for (const [tx, docs] of byTx) {
      let rep = docs.find(d => (d.lambda === 'oracle-ohip') && String(d.direction || '').toLowerCase() === 'exit');
      if (!rep) {
        const ends = docs.filter(d => ['exit', 'error'].includes(String(d.direction || '').toLowerCase()));
        rep = ends.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))[0];
      }
      if (!rep) rep = docs[docs.length - 1];
      const ms = (rep.data && rep.data.messageStatus) || (rep.status === 'error' ? 'ERROR' : rep.status === 'success' ? 'PROCESSED' : (rep.status || '').toUpperCase());
      out.push({ tx, rep, status: ms, at: rep.timestamp });
    }
    out.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
    return out;
  }, [fullLog]);

  // Seamless transcripts: the moment an offloaded entry is on-screen and expanded, pull it from S3 in
  // the background — the user should never see (or click) a "load from S3" affordance. hydrateEntry is a
  // no-op once the payload is loaded or loading, so this is safe to run on every render pass.
  useEffect(() => {
    const autoHydrate = (entry, key, defExpanded) => {
      if (!entry || !entry.payload_offloaded) return;
      const dir = String(entry.direction || '').toLowerCase();
      const def = defExpanded != null ? defExpanded : (dir !== 'entry' && dir !== 'exit');
      if (expanded[key] ?? def) hydrateEntry(entry);
    };
    if (tab === 'history') attempts.forEach(a => autoHydrate(a.rep, `hist-${a.tx}`, false));
    else orderedLog.forEach((entry, i) => autoHydrate(entry, i));
  }, [tab, orderedLog, attempts, expanded]); // eslint-disable-line

  const renderHistory = (rec) => {
    if (!rec) return <p className="message-log-empty">Record not found in the current list — history unavailable.</p>;
    const dataPoints = [
      ['Source Data Point 1', rec.sourceDataPoint1],
      ['Source Data Point 2', rec.sourceDataPoint2],
      ['Source Data Point 3', rec.sourceDataPoint3],
    ].filter(([, v]) => v != null && String(v).trim() !== '');
    // Manual operator notes / DVM auto-notes / simulator runs live in status_history (tagged
    // [note]/[dvm]/[sim]); they are not pipeline attempts, so surface them separately above the
    // per-attempt cards. A [sim] entry carries the request body it sent in `request` — that is the
    // whole point of recording it, so it is rendered rather than just named.
    const notes = (Array.isArray(rec.statusHistory) ? rec.statusHistory : []).filter(h => {
      const c = String(h.comment || '').trim();
      return c.startsWith('[note]') || c.startsWith('[dvm]') || c.startsWith('[sim]');
    });
    return (
      <div className="recon-detail">
        {dataPoints.length > 0 && (
          <div className="recon-detail-section">
            <div className="recon-detail-title">Source Data Points</div>
            <div className="recon-dp-grid">
              {dataPoints.map(([label, val]) => (
                <div key={label} className="recon-dp">
                  <span className="recon-dp-label">{label}</span>
                  <span className="recon-dp-val">{val}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {notes.length > 0 && (
          <div className="recon-detail-section">
            <div className="recon-detail-title">Notes</div>
            <table className="recon-history-table">
              <tbody>
                {notes.slice().reverse().map((h, i) => (
                  <tr key={i}>
                    <td className="recon-history-comment">
                      {h.comment || '-'}
                      {h.request && (
                        <details className="recon-history-body">
                          <summary>Request body sent</summary>
                          <pre className="story-exchange-pre">{prettyJson(h.request)}</pre>
                        </details>
                      )}
                    </td>
                    <td className="recon-history-at">{h.at ? formatDateTime(h.at) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="recon-detail-section">
          <div className="recon-detail-title">Requests ({attempts.length})</div>
          {logLoading ? (
            <div className="message-log-loading"><Spinner /></div>
          ) : attempts.length === 0 ? (
            <div className="recon-detail-empty">No request transcripts recorded for this record yet.</div>
          ) : (
            <div className="message-log-entries">
              {attempts.map((a) => renderLogEntry(a.rep, `hist-${a.tx}`, {
                defaultExpanded: false,
                headerContent: (
                  <>
                    <span className={`status-badge ${getReconStatusColor(a.status)}`}>{a.status || '-'}</span>
                    <span className="message-log-lambda">{a.rep.lambda || 'oracle-ohip'}</span>
                    <span className="message-log-time">{a.at ? new Date(a.at).toLocaleString() : ''}</span>
                  </>
                ),
              }))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleAddComment = async () => {
    const text = commentText.trim();
    if (!text || commentSaving) return;
    setCommentSaving(true);
    try {
      await syncService.addSprComment(syncId, taskId, recordUuid, `${SPR_NOTE_PREFIX} ${text}`);
      showToast('Comment added', 'success');
      setCommentOpen(false);
      setCommentText('');
      await fetchRecords();      // refresh so the Status History tab shows the new entry
      setTab('history');
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Failed to add comment', 'error');
    } finally {
      setCommentSaving(false);
    }
  };

  const title = record?.sourcerecordid ? `Source: ${record.sourcerecordid}` : `UUID: ${recordUuid}`;

  return (
    <div className="mlp-page">
      {flash && <div className={`mlp-flash mlp-flash-${String(flash).toLowerCase()}`} aria-hidden="true" />}
      <button className="pp-back-btn" onClick={() => navigate(backTo)}>← Back to records</button>

      <div className="mlp-header">
        <div>
          <h1 className="mlp-title">
            Message Log
            {record?.recordStatus && (
              <span className={`mlp-status-big ${getReconStatusColor(record.recordStatus)}`} title={getRecordStatusTitle(record.recordStatus)}>{record.recordStatus}</span>
            )}
          </h1>
          <p className="mlp-subtitle">
            {title}
            {record?.targetRecordId && ` → Target: ${record.targetRecordId}`}
          </p>
        </div>
        <div className="mlp-actions">
          <button
            type="button"
            className={`pp-toggle-btn pp-toggle-btn-sm ${autoRefresh ? 'pp-toggle-btn-on' : 'pp-toggle-btn-off'}`}
            onClick={() => setAutoRefresh(v => !v)}
            title={autoRefresh ? 'Auto-refresh is on — record + log reload every 5s' : 'Turn on to reload the record + log every 5s'}
          >
            <span className="pp-toggle-track"><span className="pp-toggle-thumb" /></span>
            <span className="pp-toggle-label">Auto-refresh 5s</span>
          </button>
          <Button variant="secondary" onClick={handleRefresh} disabled={refreshing} title="Reload the record + message log now">
            <RefreshCw className={`icon-sm${refreshing ? ' mlp-spin' : ''}`} /> {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Button variant="secondary" onClick={() => goToSibling(-1)} disabled={recordIndex <= 0}>
            <ChevronLeft className="icon-sm" /> Prev
          </Button>
          {recordIndex >= 0 && <span className="message-log-nav-position">{recordIndex + 1} of {records.length}</span>}
          <Button variant="secondary" onClick={() => goToSibling(1)} disabled={recordIndex < 0 || recordIndex >= records.length - 1}>
            Next <ChevronRight className="icon-sm" />
          </Button>
          {record && (
            <Button variant="secondary" onClick={() => { setCommentText(''); setCommentOpen(true); }}>
              <MessageSquarePlus className="icon-sm" /> Add comment
            </Button>
          )}
          {record?.recordid?.trim() && (
            <Button variant="primary" disabled={reproRunning} onClick={handleReprocess}>
              <RefreshCw className="icon-sm" /> {reproRunning ? 'Reprocessing…' : 'Reprocess'}
            </Button>
          )}
          {isSuperAdmin && (
            <Button variant="secondary" className="sa-only" disabled={curlExporting || logLoading} onClick={exportOhipCurls}
                    title="Download every request this record made to OHIP, oldest first, as a bash script of curls (no credentials) — for Oracle support">
              <Terminal className="icon-sm" /> {curlExporting ? 'Exporting…' : 'OHIP requests (curl)'}
            </Button>
          )}
          {isSuperAdmin && record && (
            <Button variant="secondary" className="sa-only" onClick={() => setEditOpen(true)}>
              <Edit className="icon-sm" /> Edit
            </Button>
          )}
          {isSuperAdmin && (
            <Button
              variant="secondary"
              className={`sa-only${currentInstruction ? ' mlp-instr-set' : ''}`}
              disabled={instrSaving}
              onClick={openInstrModal}
            >
              <ClipboardList className="icon-sm" /> {instrSaving ? 'Saving…' : currentInstruction ? 'Site instruction ✓' : 'Add instruction for site'}
            </Button>
          )}
          {supportTicketId ? (
            <Link className="btn btn-secondary" to={`/support/${supportTicketId}`} title="Open the support ticket for this record">
              <LifeBuoy className="icon-sm" /> Support #{supportTicketId}
            </Link>
          ) : (
            <SupportButton submit={raiseRecordTicket}
              defaultSubject={record?.sourcerecordid ? `${record.scopetype || 'Record'} ${record.sourcerecordid}: ` : ''} />
          )}
          {isSuperAdmin && (record?.jiraKey || record?.jira_key) && (
            <a className="btn btn-secondary sa-only mlp-jira-link" href={`https://hconnect.atlassian.net/browse/${record.jiraKey || record.jira_key}`} target="_blank" rel="noreferrer" title="The Jira issue (superadmin)">
              <ExternalLink className="icon-sm" /> {record.jiraKey || record.jira_key}
            </a>
          )}
        </div>
      </div>

      {/* The record actions (Reprocess, Add comment, Edit) all hang off `record`, which is only ever found
          in THIS task's record list. That list is scoped to the task's own scope type server-side —
          getReconciliationRecordsFromSpr takes scopeType from getScopeType(taskId) and ignores any
          scopeType filter sent by the client — so a BLOCK record opened under, say, the profiles task is
          simply not in it. Every action then vanished with no explanation: the page still rendered the
          message log (fetched by uuid, which is not task-scoped) and looked merely broken. Say so
          instead. */}
      {recordsLoaded && !record && (
        <div className="mlp-notfound">
          <AlertCircle className="icon-sm" />
          <span>
            This record is not part of this task, so Reprocess, Add comment and Edit are unavailable.
            The message log below is the right record — open it from the task that owns its scope
            (the one whose reconciliation list contains it) to act on it.
          </span>
        </div>
      )}
      {/* Shown even when one view loaded: Status History failing while Messages worked is invisible
          until the reader opens that tab and draws the wrong conclusion from an empty one. */}
      {logError && <div className="error-banner"><AlertCircle className="icon-sm" />{logError}</div>}
      {instrError && <div className="error-banner"><AlertCircle className="icon-sm" />{instrError}</div>}

      <div className="mlp-tabs">
        <button className={`mlp-tab${tab === 'messages' ? ' mlp-tab-active' : ''}`} onClick={() => setTab('messages')}>Messages</button>
        <button className={`mlp-tab${tab === 'history' ? ' mlp-tab-active' : ''}`} onClick={() => setTab('history')}>Status History</button>
      </div>

      <div className="mlp-body">
        {tab === 'history' ? (
          <div className="message-log-history">{renderHistory(record)}</div>
        ) : logLoading ? (
          <div className="message-log-loading"><Spinner /></div>
        ) : orderedLog.length === 0 ? (
          // A failed fetch and a record with no entries look identical unless the page says so. The
          // first is a fault to chase; the second is a fact about the record.
          logError ? (
            <div className="error-banner"><AlertCircle className="icon-sm" />{logError}</div>
          ) : (
            <p className="message-log-empty">No message log entries found for this record.</p>
          )
        ) : (
          <>
            {/* Run selector. The log keeps every reprocess, so without this the view mixed runs and old
                errors read as current. Newest is selected by default. */}
            {logRuns.length > 1 && (
              <div className="mlp-runbar">
                <span className="mlp-runbar-label">Run</span>
                {logRuns.map((r, idx) => (
                  <button
                    key={r.tx}
                    className={`mlp-runchip${r.tx === activeRunTx ? ' mlp-runchip-active' : ''}${r.hasError ? ' mlp-runchip-error' : ''}`}
                    onClick={() => setSelectedRunTx(r.tx)}
                    title={`${r.count} entries · ${r.tx}`}
                  >
                    {idx === 0 ? 'Latest' : `#${logRuns.length - idx}`}
                    <span className="mlp-runchip-time">
                      {r.last ? new Date(r.last).toLocaleString() : ''}
                    </span>
                    {r.hasError && <span className="mlp-runchip-dot" title="this run errored">●</span>}
                  </button>
                ))}
              </div>
            )}
            <div className="message-log-entries">
              {orderedLog.map((entry, i) => renderLogEntry(entry, i))}
            </div>
          </>
        )}
      </div>

      {instrModalOpen && (
        <ConfirmModal
          title={currentInstruction ? 'Edit site instruction' : 'Add instruction for site'}
          message={`The instruction is stamped on ${record?.sourcerecordid || recordUuid} and included in the On-site Instructions report, CSV and email for this task. Clear the text to remove it.`}
          confirmLabel="Save instruction"
          onConfirm={saveInstruction}
          onCancel={() => setInstrModalOpen(false)}
        >
          <textarea
            className="cm-textarea"
            value={instrText}
            onChange={e => setInstrText(e.target.value)}
            placeholder="e.g. Re-key the payment card on this reservation at the front desk"
            autoFocus
          />
        </ConfirmModal>
      )}

      {reproWizardItems && (
        <ReprocessProfilesModal
          items={reproWizardItems}
          otherCount={0}
          processing={reproRunning}
          onCancel={() => setReproWizardItems(null)}
          onConfirm={(profileIds) => runReprocess(profileIds)}
        />
      )}

      {reproConfirmOpen && (
        <ConfirmModal
          title="Reprocess record"
          message={`Re-request ${record?.sourcerecordid || recordUuid} from the source and run it through the pipeline again?`}
          confirmLabel="Reprocess"
          onConfirm={() => runReprocess()}
          onCancel={() => setReproConfirmOpen(false)}
        />
      )}

      {editOpen && record && (
        <RecordEditModal
          record={record}
          onClose={() => setEditOpen(false)}
          onSave={async (fields) => {
            await syncService.updateReconciliationRecordFields(syncId, taskId, recordUuid, fields);
            showToast('Record updated', 'success');
            setEditOpen(false);
            await fetchRecords();
          }}
        />
      )}

      {simulateSeed && (
        <OhipSimulateModal
          seed={simulateSeed}
          syncId={syncId}
          taskId={taskId}
          onClose={() => setSimulateSeed(null)}
        />
      )}

      {dvmSeed && (
        <DvmModal
          seed={dvmSeed}
          syncId={syncId}
          taskId={taskId}
          recordUuid={recordUuid}
          onClose={() => setDvmSeed(null)}
        />
      )}

      {diffFor && (() => {
        const left = entryPayloadText(diffFor.entry);
        const right = entryPayloadText(diffFor.exit);
        // Either side still coming from S3. Showing DiffModal now would render "Identical" over two
        // empty documents, which is a wrong answer rather than a missing one.
        if (left === null || right === null) {
          return (
            <div className="pp-modal-backdrop" onClick={() => setDiffFor(null)}>
              <div className="pp-modal diff-modal" onClick={e => e.stopPropagation()}>
                <div className="pp-modal-header">
                  <span>Entry → Exit</span>
                  <button className="pp-modal-close" onClick={() => setDiffFor(null)} title="Close">×</button>
                </div>
                <div className="pp-modal-body">
                  <div className="pp-modal-empty"><Spinner /> Loading payloads…</div>
                </div>
              </div>
            </div>
          );
        }
        return (
          <DiffModal
            title="Entry → Exit"
            subtitle={diffFor.entry.lambda || diffFor.entry.lambdaName || ''}
            leftLabel="Entry"
            rightLabel="Exit"
            left={left}
            right={right}
            onClose={() => setDiffFor(null)}
          />
        );
      })()}

      {commentOpen && (
        <ConfirmModal
          title="Add comment"
          message="Adds a comment to this record's status history. It appears in the Status History tab and the reconciliation report."
          confirmLabel={commentSaving ? 'Saving…' : 'Add comment'}
          onConfirm={handleAddComment}
          onCancel={() => { setCommentOpen(false); setCommentText(''); }}
        >
          <textarea
            className="form-input"
            style={{ width: '100%', minHeight: 90, resize: 'vertical' }}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="e.g. Created the missing NEWS/FIN preference mappings on OHIP"
            autoFocus
          />
        </ConfirmModal>
      )}

      {kbModalElement}

      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            {toast.type === 'success' && <CheckCircle className="toast-icon" />}
            {toast.type === 'error' && <AlertCircle className="toast-icon" />}
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessageLogPage;
